import {
  Cell,
  Checkbox,
  Section,
  Button,
  Headline,
  Input,
  Select,
  Subheadline,
  Radio,
} from "tmaui";
import styles from "./Sign.module.css";
import { useSign } from "../../hooks/useSign";

interface FormSectionProps {
  startParam?: string | null;
}

export const FormSection = ({ startParam }: FormSectionProps) => {
  const { handleSubmit, isLoading, isReady } = useSign();

  return (
    <>
      <div className={styles.container}>
        <div className={styles.headerSection}>
          <div className={styles.headerText}>
            <Headline weight="2">{startParam || "com.teflo.unknown"}</Headline>
          </div>
        </div>

        <Input header="Bundle Name" placeholder="Enter app name" />

        <Section header="Certificates">
          <Cell
            Component="label"
            before={
              <Radio name="certificate" value="distribution" defaultChecked />
            }
            description="Use a distribution certificate, this is the default and recommended option, this is the only certificate that allows notifications to work."
            multiline
          >
            Distribution
          </Cell>
          <Cell
            Component="label"
            before={<Radio name="certificate" value="development" />}
            description="Use a development certificate, some features only work with development certificates, such as changing app icons, family controls and more."
            multiline
          >
            Development
          </Cell>
        </Section>

        <Section header="Bundle Identifiers">
          <Cell
            Component="label"
            before={<Checkbox name="encode_ids" defaultChecked />}
            description="Modify application identifiers, needed to sign apps you do not own"
            multiline
          >
            Encode App ID's
          </Cell>
          <Cell
            Component="label"
            before={<Checkbox name="patch_ids" defaultChecked />}
            description="Apply patches to property lists and binaries. Solves situations where the app tries to use its original bundle ID"
            multiline
          >
            Patch ID's
          </Cell>
          <Cell
            Component="label"
            before={<Checkbox name="force_original_id" />}
            description="Maintain original bundle identifier. Can fix notifications with a Distribution certificate."
            multiline
          >
            Force Original Bundle ID
          </Cell>
        </Section>

        <Section header="Features">
          <Cell
            Component="label"
            before={<Checkbox name="patch_debug" />}
            description="Enable debug features (requires development certificate)"
            multiline
          >
            Debug Mode
          </Cell>
          <Cell
            Component="label"
            before={<Checkbox name="patch_all_devices" />}
            description="Enable support for all devices and lower minimum OS version"
            multiline
          >
            All Devices Support
          </Cell>
          <Cell
            Component="label"
            before={<Checkbox name="patch_file_sharing" />}
            description="Enable Files app and iTunes file sharing support"
            multiline
          >
            File Sharing
          </Cell>
          <Cell
            Component="label"
            before={<Checkbox name="patch_game_mode" />}
            description="Enable Game Mode support"
            multiline
          >
            Game Mode
          </Cell>
          <Cell
            Component="label"
            before={<Checkbox name="patch_promotion" />}
            description="Force ProMotion/120Hz support (may not work properly)"
            multiline
          >
            ProMotion Support
          </Cell>
          <Cell
            Component="label"
            before={<Checkbox name="patch_fullscreen" />}
            description="Force fullscreen mode on iPad (disable multitasking)"
            multiline
          >
            Fullscreen Mode
          </Cell>
          <Cell
            Component="label"
            before={<Checkbox name="patch_orientation" />}
            description="Force app to allow all orientations"
            multiline
          >
            All Orientations
          </Cell>
          <Cell
            Component="label"
            before={<Checkbox name="patch_liquid_glass" />}
            description="Force Liquid Glass support (may not work properly)"
            multiline
          >
            Liquid Glass
          </Cell>
          <Cell
            Component="label"
            before={<Checkbox name="remove_url_schemes" />}
            description="Remove URL schemes registration"
            multiline
          >
            Remove URL Schemes
          </Cell>
          <Select name="patch_status_bar" header="Status Bar Style">
            <option value="default">Default</option>
            <option value="hidden">Hidden</option>
            <option value="light">Light</option>
            <option value="dark">Dark</option>
          </Select>
          <Select name="patch_user_interface_style" header="Interface Style">
            <option value="default">Default</option>
            <option value="light">Light Mode</option>
            <option value="dark">Dark Mode</option>
          </Select>
        </Section>

        <Section header="✨ Experimental">
          <Subheadline level="1" weight="3" className={styles.warningText}>
            Warning: These experimental features may bite. Use at your own
            peril!
          </Subheadline>
          <Cell
            Component="label"
            before={<Checkbox name="inject_warpsign_fix" />}
            description="Inject Warpsign Fix dylib, a swiss army knife of sideloading fixes."
            multiline
          >
            Inject Warpsign Fix
          </Cell>
          <Cell
            Component="label"
            before={<Checkbox name="hide_home_indicator" />}
            description="Hide home indicator on iPhone X and newer devices"
            multiline
          >
            Hide Home Indicator
          </Cell>
        </Section>
      </div>

      <div className={styles.buttonWrapper}>
        <Button
          mode="filled"
          size="l"
          className={styles.fixedButton}
          onClick={handleSubmit}
          loading={isLoading}
          disabled={!isReady || isLoading}
        >
          {isLoading ? "Sending..." : "Sign App"}
        </Button>
      </div>
    </>
  );
};
